import { useCallback, useRef, useState } from "react";
import type { ConversationSummary } from "../types";
import { fetchWithTokenRefresh } from "../fetchWithTokenRefresh";

// Simple, fast, first-party DB-backed routes with no external provider in the chain — 15s is
// generous defensive slack, not a tuned SLA (shorter than /speech-to-text's 20s timeout).
const REQUEST_TIMEOUT_MS = 15_000;

export interface UseChatSessionsOptions {
  endpoint: string;
  buildHeaders: () => Record<string, string>;
  /** Re-exchanges a fresh RPT for the current property — see property-provider.tsx. */
  refreshToken: () => Promise<string>;
}

export interface UseChatSessionsResult {
  sessions: ConversationSummary[];
  loading: boolean;
  error: string | null;
  listSessions: () => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
}

export function useChatSessions({
  endpoint,
  buildHeaders,
  refreshToken,
}: UseChatSessionsOptions): UseChatSessionsResult {
  const [sessions, setSessions] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Latest values live in refs so an in-flight request never has to be torn down just
  // because a prop changed on a re-render.
  const endpointRef = useRef(endpoint);
  const buildHeadersRef = useRef(buildHeaders);
  const refreshTokenRef = useRef(refreshToken);
  endpointRef.current = endpoint;
  buildHeadersRef.current = buildHeaders;
  refreshTokenRef.current = refreshToken;

  const listAbortControllerRef = useRef<AbortController | null>(null);

  const listSessions = useCallback(async () => {
    // Supersede a stale in-flight call — the history view can plausibly be closed and
    // reopened faster than one GET /chat/sessions round-trip.
    listAbortControllerRef.current?.abort();
    const controller = new AbortController();
    listAbortControllerRef.current = controller;
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);

    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithTokenRefresh(
        `${endpointRef.current}/chat/sessions`,
        {
          method: "GET",
          headers: buildHeadersRef.current(),
          signal: controller.signal,
        },
        refreshTokenRef.current
      );
      if (!response.ok) {
        setError(`Failed to load chat sessions (${response.status})`);
        return;
      }
      const data = await response.json();
      setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
    } catch (err) {
      // A superseded call's abort must stay silent — only the newest call's outcome
      // should be reflected in state.
      if (controller.signal.aborted && !timedOut) return;
      setError(timedOut ? "Request timed out" : "Failed to load chat sessions");
      console.error("[AIAssistant] Failed to list chat sessions", err);
    } finally {
      clearTimeout(timeoutId);
      if (listAbortControllerRef.current === controller) listAbortControllerRef.current = null;
      setLoading(false);
    }
  }, []);

  const deleteSession = useCallback(async (id: string) => {
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);

    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithTokenRefresh(
        `${endpointRef.current}/chat/sessions/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
          headers: buildHeadersRef.current(),
          signal: controller.signal,
        },
        refreshTokenRef.current
      );
      if (!response.ok) {
        const message = `Failed to delete chat session (${response.status})`;
        setError(message);
        throw new Error(message);
      }
    } catch (err) {
      // Logging is the caller's responsibility (ai-assistant-sidebar.tsx's
      // handleDeleteSession already logs "[AIAssistant] Failed to delete chat session"
      // on rejection) — this hook only sets `error` and re-throws, avoiding a duplicate log.
      if (err instanceof DOMException && err.name === "AbortError" && timedOut) {
        setError("Request timed out");
        throw new Error("Request timed out");
      }
      if (!(err instanceof Error)) {
        setError("Failed to delete chat session");
        throw new Error("Failed to delete chat session");
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);

  return { sessions, loading, error, listSessions, deleteSession };
}
